import { formatFileSize } from '~/lib/hero-image';

/**
 * Google's front end refuses any request over 32 MiB with an HTML 413 page,
 * after the whole body has uploaded and before hhOS sees it, so a form that
 * posts photos fails with "Unable to decode turbo-stream response" (HHOS-2595).
 * 30 MB leaves room for the rest of the form.
 */
export const REQUEST_UPLOAD_MAX_BYTES = 30_000_000;

/**
 * Splits `incoming` into the files that fit beside `alreadyBytes` and those
 * that don't, in order. A file that doesn't fit is skipped, not the rest, so a
 * smaller file after it can still go in.
 */
export function fitWithinUploadLimit(
  alreadyBytes: number,
  incoming: File[],
  maxBytes = REQUEST_UPLOAD_MAX_BYTES
): { accepted: File[]; refused: File[] } {
  const accepted: File[] = [];
  const refused: File[] = [];
  let total = alreadyBytes;
  for (const file of incoming) {
    if (total + file.size <= maxBytes) {
      accepted.push(file);
      total += file.size;
    } else {
      refused.push(file);
    }
  }
  return { accepted, refused };
}

export function totalBytes(files: File[]): number {
  return files.reduce((sum, f) => sum + f.size, 0);
}

/** Whether all of `incoming` fits beside `alreadyBytes` as it is. */
export function fitsUploadLimit(
  alreadyBytes: number,
  incoming: File[],
  maxBytes = REQUEST_UPLOAD_MAX_BYTES
): boolean {
  return alreadyBytes + totalBytes(incoming) <= maxBytes;
}

export function uploadLimitLabel(maxBytes = REQUEST_UPLOAD_MAX_BYTES): string {
  return `${Math.round(maxBytes / 1_000_000)} MB`;
}

export function uploadLimitMessage(refused: File[], maxBytes = REQUEST_UPLOAD_MAX_BYTES): string {
  const limit = uploadLimitLabel(maxBytes);
  const tooBigAlone = refused.find((f) => f.size > maxBytes);
  if (refused.length === 1 && tooBigAlone) {
    return `${tooBigAlone.name} is ${formatFileSize(tooBigAlone.size)}, over the ${limit} upload limit.`;
  }
  const what = refused.length === 1 ? refused[0].name : `${refused.length} files`;
  const next = tooBigAlone ? '' : ' Save, then add the rest.';
  return `${what} not added: one save can upload up to ${limit}.${next}`;
}
